import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { todayIsoDate } from '@/lib/formatters';
import { useBomGraph } from '@/features/manufacturing/boms';
import { useInventoryLevels, useItems } from '@/features/inventory/hooks';
import { usePurchaseOrders } from '@/features/procurement/hooks';
import { runPlanning } from './engine';
import type {
  LeadTimeSample,
  OpenSupply,
  PlanLine,
  PlanLineKind,
  PlanningInputs,
  PlanningItem,
  PlanningModel,
  PlanningSettings,
  PlanningSupplier,
} from './types';

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export const DEFAULT_PLANNING_SETTINGS: PlanningSettings = {
  workingDays: [1, 2, 3, 4, 5],
  horizonDays: 120,
  safetyBufferDays: 5,
  receivingBufferDays: 0,
  orderCoverageDays: 30,
  safetyStockDays: 0,
  monthlyPurchasingBudgetCents: null,
};

interface PlanningSettingsRow {
  organization_id: string;
  working_days: number[];
  horizon_days: number;
  safety_buffer_days: number;
  receiving_buffer_days: number;
  order_coverage_days: number;
  safety_stock_days: number;
  monthly_purchasing_budget_cents: number | null;
}

export const PLANNING_SETTINGS_KEY = ['planning-settings'] as const;

export function usePlanningSettings() {
  return useQuery({
    queryKey: PLANNING_SETTINGS_KEY,
    queryFn: async (): Promise<{ settings: PlanningSettings; saved: boolean }> => {
      const { data, error } = await supabase
        .from('production_planning_settings' as never)
        .select('*')
        .maybeSingle();
      if (error) throw error;
      const row = data as unknown as PlanningSettingsRow | null;
      if (!row) return { settings: DEFAULT_PLANNING_SETTINGS, saved: false };
      return {
        saved: true,
        settings: {
          workingDays: row.working_days ?? DEFAULT_PLANNING_SETTINGS.workingDays,
          horizonDays: row.horizon_days,
          safetyBufferDays: row.safety_buffer_days,
          receivingBufferDays: row.receiving_buffer_days,
          orderCoverageDays: row.order_coverage_days,
          safetyStockDays: row.safety_stock_days,
          monthlyPurchasingBudgetCents: row.monthly_purchasing_budget_cents,
        },
      };
    },
  });
}

export function useSavePlanningSettings() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (settings: PlanningSettings) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      if (settings.workingDays.length === 0) throw new Error('Pick at least one working day.');
      const { error } = await supabase.from('production_planning_settings' as never).upsert(
        {
          organization_id: profile.organization_id,
          working_days: settings.workingDays,
          horizon_days: settings.horizonDays,
          safety_buffer_days: settings.safetyBufferDays,
          receiving_buffer_days: settings.receivingBufferDays,
          order_coverage_days: settings.orderCoverageDays,
          safety_stock_days: settings.safetyStockDays,
          monthly_purchasing_budget_cents: settings.monthlyPurchasingBudgetCents,
        } as never,
        { onConflict: 'organization_id' },
      );
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLANNING_SETTINGS_KEY }),
  });
}

// ---------------------------------------------------------------------------
// Holidays
// ---------------------------------------------------------------------------

export interface PlanningHoliday {
  id: string;
  holiday_date: string;
  name: string;
}

export const PLANNING_HOLIDAYS_KEY = ['planning-holidays'] as const;

export function usePlanningHolidays() {
  return useQuery({
    queryKey: PLANNING_HOLIDAYS_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('planning_holidays' as never)
        .select('id, holiday_date, name')
        .order('holiday_date');
      if (error) throw error;
      return data as unknown as PlanningHoliday[];
    },
  });
}

export function useAddPlanningHoliday() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { date: string; name: string }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const name = input.name.trim();
      if (!input.date) throw new Error('Pick a date.');
      if (!name) throw new Error('Give the holiday a name.');
      const { error } = await supabase.from('planning_holidays' as never).upsert(
        { organization_id: profile.organization_id, holiday_date: input.date, name } as never,
        { onConflict: 'organization_id,holiday_date' },
      );
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLANNING_HOLIDAYS_KEY }),
  });
}

export function useDeletePlanningHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('planning_holidays' as never).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLANNING_HOLIDAYS_KEY }),
  });
}

// ---------------------------------------------------------------------------
// Plan lines
// ---------------------------------------------------------------------------

export interface PlanLineRow {
  id: string;
  item_id: string;
  kind: PlanLineKind;
  quantity: number;
  start_date: string | null;
  end_date: string | null;
  net_against_stock: boolean;
  source: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  items: { id: string; sku: string; name: string; base_uom: string } | null;
}

export const PLAN_LINES_KEY = ['production-plan-lines'] as const;

export const PLAN_LINE_KIND_LABELS: Record<PlanLineKind, string> = {
  batch: 'Batch by a date',
  daily: 'Every working day',
  monthly: 'Monthly target',
};

export function usePlanLines() {
  return useQuery({
    queryKey: PLAN_LINES_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('production_plan_lines' as never)
        .select('*, items:item_id(id, sku, name, base_uom)')
        .order('created_at');
      if (error) throw error;
      return (data as unknown as PlanLineRow[]).map((row) => ({
        ...row,
        quantity: Number(row.quantity),
      }));
    },
  });
}

export interface SavePlanLineInput {
  id?: string;
  itemId: string;
  kind: PlanLineKind;
  quantity: number;
  startDate: string | null;
  endDate: string | null;
  netAgainstStock: boolean;
  source: string | null;
  notes: string | null;
  isActive?: boolean;
}

export function useSavePlanLine() {
  const qc = useQueryClient();
  const { profile, user } = useAuth();
  return useMutation({
    mutationFn: async (input: SavePlanLineInput) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      if (!input.itemId) throw new Error('Pick what to build.');
      if (!Number.isFinite(input.quantity) || input.quantity <= 0) {
        throw new Error('Quantity must be greater than zero.');
      }
      if (input.kind === 'batch' && !input.endDate) throw new Error('A batch needs a due date.');
      if (input.kind === 'monthly' && !input.startDate) throw new Error('Pick the month.');
      if (input.startDate && input.endDate && input.endDate < input.startDate) {
        throw new Error('The end date is before the start date.');
      }
      const payload = {
        organization_id: profile.organization_id,
        item_id: input.itemId,
        kind: input.kind,
        quantity: input.quantity,
        start_date: input.startDate,
        end_date: input.kind === 'monthly' ? null : input.endDate,
        net_against_stock: input.netAgainstStock,
        source: input.source?.trim() || null,
        notes: input.notes?.trim() || null,
        is_active: input.isActive ?? true,
      };
      const query = input.id
        ? supabase.from('production_plan_lines' as never).update(payload as never).eq('id', input.id)
        : supabase
            .from('production_plan_lines' as never)
            .insert({ ...payload, created_by: user?.id ?? null } as never);
      const { error } = await query;
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAN_LINES_KEY }),
  });
}

export function useSetPlanLineActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('production_plan_lines' as never)
        .update({ is_active: input.isActive } as never)
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAN_LINES_KEY }),
  });
}

export function useDeletePlanLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('production_plan_lines' as never).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PLAN_LINES_KEY }),
  });
}

// ---------------------------------------------------------------------------
// Suppliers + price breaks (whole org, for the planner)
// ---------------------------------------------------------------------------

interface PlanningSupplierRow {
  id: string;
  item_id: string;
  vendor_id: string;
  is_primary: boolean;
  unit_cost_cents: number | null;
  lead_time_days: number | null;
  min_order_qty: number | null;
  order_multiple: number | null;
  purchase_uom: string | null;
  units_per_purchase_uom: number | string | null;
  vendors: { id: string; name: string } | null;
  item_supplier_price_breaks: Array<{ id: string; min_qty: number; unit_cost_cents: number }> | null;
}

export const PLANNING_SUPPLIERS_KEY = ['planning-suppliers'] as const;

export function usePlanningSuppliers() {
  return useQuery({
    queryKey: PLANNING_SUPPLIERS_KEY,
    queryFn: async (): Promise<PlanningSupplier[]> => {
      const { data, error } = await supabase
        .from('item_suppliers' as never)
        .select(
          'id, item_id, vendor_id, is_primary, unit_cost_cents, lead_time_days, min_order_qty, order_multiple, purchase_uom, units_per_purchase_uom, vendors:vendor_id(id, name), item_supplier_price_breaks(id, min_qty, unit_cost_cents)',
        );
      if (error) throw error;
      return (data as unknown as PlanningSupplierRow[]).map((row) => ({
        id: row.id,
        itemId: row.item_id,
        vendorId: row.vendor_id,
        vendorName: row.vendors?.name ?? 'Vendor',
        isPrimary: row.is_primary,
        purchaseUom: row.purchase_uom?.trim() || null,
        unitsPerPurchaseUom: row.purchase_uom ? Number(row.units_per_purchase_uom ?? 1) || 1 : 1,
        unitCostCents: row.unit_cost_cents,
        leadTimeDays: row.lead_time_days,
        minOrderQty: row.min_order_qty == null ? null : Number(row.min_order_qty),
        orderMultiple: row.order_multiple == null ? null : Number(row.order_multiple),
        priceBreaks: (row.item_supplier_price_breaks ?? [])
          .map((b) => ({ minQty: Number(b.min_qty), unitCostCents: Number(b.unit_cost_cents) }))
          .sort((a, b) => a.minQty - b.minQty),
      }));
    },
  });
}

// ---------------------------------------------------------------------------
// Lead-time history: ordered → first received, per PO line
// ---------------------------------------------------------------------------

interface LeadTimeSampleRow {
  po_line_id: string | null;
  item_id: string | null;
  goods_receipts: {
    received_at: string;
    status: string | null;
    purchase_orders: {
      po_number: string;
      vendor_id: string;
      ordered_at: string | null;
    } | null;
  } | null;
}

export const LEAD_TIME_SAMPLES_KEY = ['planning-lead-time-samples'] as const;

export function useLeadTimeSamples() {
  return useQuery({
    queryKey: LEAD_TIME_SAMPLES_KEY,
    queryFn: async (): Promise<LeadTimeSample[]> => {
      const { data, error } = await supabase
        .from('gr_lines' as never)
        .select(
          'po_line_id, item_id, goods_receipts!inner(received_at, status, purchase_orders(po_number, vendor_id, ordered_at))',
        )
        .limit(5000);
      if (error) throw error;
      // One sample per PO line: the first delivery is the lead time, later
      // partial deliveries are not.
      const firstByLine = new Map<string, LeadTimeSample>();
      for (const row of data as unknown as LeadTimeSampleRow[]) {
        const gr = row.goods_receipts;
        const po = gr?.purchase_orders;
        if (!row.po_line_id || !row.item_id || !gr || !po?.ordered_at) continue;
        if (gr.status === 'reversed') continue;
        const receivedAt = gr.received_at.slice(0, 10);
        const existing = firstByLine.get(row.po_line_id);
        if (existing && existing.receivedAt <= receivedAt) continue;
        firstByLine.set(row.po_line_id, {
          itemId: row.item_id,
          vendorId: po.vendor_id,
          orderedAt: po.ordered_at.slice(0, 10),
          receivedAt,
          poNumber: po.po_number,
        });
      }
      return [...firstByLine.values()];
    },
  });
}

// ---------------------------------------------------------------------------
// Draft PO creation
// ---------------------------------------------------------------------------

export interface PlannedPoLineInput {
  item_id: string;
  /** In `uom` (the supplier's purchase unit when it has one). */
  quantity: number;
  unit_price_cents: number | null;
  uom?: string | null;
  /** Stock units per `uom`. */
  conversion_to_base?: number | null;
  description?: string | null;
}

export interface PlannedPoInput {
  vendor_id: string;
  order_by_date: string | null;
  expected_at: string | null;
  lead_time_days: number | null;
  ship_to_location_id?: string | null;
  notes?: string | null;
  lines: PlannedPoLineInput[];
}

export interface CreatedPlannedPo {
  id: string;
  po_number: string;
  vendor_id: string;
}

export function useCreatePlannedPurchaseOrders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orders: PlannedPoInput[]) => {
      if (orders.length === 0) throw new Error('Nothing to order.');
      const { data, error } = await supabase.rpc('create_planned_purchase_orders' as never, {
        p_orders: orders,
      } as never);
      if (error) throw error;
      return (data ?? []) as unknown as CreatedPlannedPo[];
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

// ---------------------------------------------------------------------------
// The model
// ---------------------------------------------------------------------------

const PLACED_OR_DRAFT = new Set(['internal_po', 'ordered', 'in_production', 'shipped', 'partially_received']);

export interface PlanningModelState {
  model: PlanningModel | null;
  isLoading: boolean;
  error: Error | null;
  settings: PlanningSettings;
  settingsSaved: boolean;
  planLines: PlanLineRow[];
  holidays: PlanningHoliday[];
}

/**
 * Everything the planner needs, loaded once and run through the engine.
 * The engine is pure and memoised on its inputs, so edits to a plan line
 * or a supplier re-plan instantly from cache.
 */
export function usePlanningModel(): PlanningModelState {
  const settingsQ = usePlanningSettings();
  const holidaysQ = usePlanningHolidays();
  const planLinesQ = usePlanLines();
  const itemsQ = useItems();
  const levelsQ = useInventoryLevels();
  const bomQ = useBomGraph();
  const suppliersQ = usePlanningSuppliers();
  const posQ = usePurchaseOrders();
  const samplesQ = useLeadTimeSamples();

  const queries = [settingsQ, holidaysQ, planLinesQ, itemsQ, levelsQ, bomQ, suppliersQ, posQ, samplesQ];
  const isLoading = queries.some((q) => q.isLoading);
  const error = (queries.find((q) => q.error)?.error as Error | undefined) ?? null;

  const settings = settingsQ.data?.settings ?? DEFAULT_PLANNING_SETTINGS;
  const today = todayIsoDate();

  const model = useMemo(() => {
    if (
      !holidaysQ.data ||
      !planLinesQ.data ||
      !itemsQ.data ||
      !levelsQ.data ||
      !bomQ.data ||
      !suppliersQ.data ||
      !posQ.data ||
      !samplesQ.data
    ) {
      return null;
    }

    const items: PlanningItem[] = itemsQ.data.map((item) => ({
      id: item.id,
      sku: item.sku,
      name: item.name,
      kind: item.kind,
      additionalKinds: item.additional_kinds ?? [],
      baseUom: item.base_uom,
      isActive: item.is_active,
      currentCostCents: bomQ.data.costByItemId.get(item.id) ?? null,
      leadTimeWorstCaseDays: item.lead_time_worst_case_days,
      leadTimeText: item.lead_time_text,
    }));

    const planLines: PlanLine[] = planLinesQ.data.map((row) => ({
      id: row.id,
      itemId: row.item_id,
      kind: row.kind,
      quantity: row.quantity,
      startDate: row.start_date,
      endDate: row.end_date,
      netAgainstStock: row.net_against_stock,
      source: row.source,
      notes: row.notes,
      isActive: row.is_active,
    }));

    const bomLines = bomQ.data.boms.flatMap((bom) =>
      bom.lines.map((line) => ({
        parentItemId: bom.item_id,
        componentItemId: line.component_item_id,
        quantity: line.quantity,
        scrapPct: line.scrap_pct,
      })),
    );

    const openSupply: OpenSupply[] = [];
    for (const po of posQ.data) {
      if (!PLACED_OR_DRAFT.has(po.status)) continue;
      for (const line of po.po_lines) {
        if (!line.item_id) continue;
        const conversion = Number(line.conversion_to_base) || 1;
        const remaining = Math.round((Number(line.quantity) - Number(line.received_quantity ?? 0)) * conversion);
        if (remaining <= 0) continue;
        openSupply.push({
          poId: po.id,
          poNumber: po.po_number,
          poLineId: line.id,
          itemId: line.item_id,
          vendorId: po.vendor_id,
          vendorName: po.vendors?.name ?? null,
          status: po.status,
          remainingQty: remaining,
          orderedAt: po.ordered_at,
          expectedAt: po.expected_at ?? po.tracking_estimated_delivery?.slice(0, 10) ?? null,
          orderByDate: (po as { order_by_date?: string | null }).order_by_date ?? null,
          leadTimeDays: po.lead_time_days,
          unitPriceCents: Number(line.unit_price_cents ?? 0) / conversion,
        });
      }
    }

    const inputs: PlanningInputs = {
      today,
      settings,
      holidays: holidaysQ.data.map((h) => h.holiday_date),
      planLines,
      items,
      onHand: levelsQ.data.map((level) => ({
        itemId: level.item_id,
        quantity: Number(level.quantity),
        locationKind: level.locations?.kind ?? null,
      })),
      bomLines,
      suppliers: suppliersQ.data,
      openSupply,
      leadTimeSamples: samplesQ.data,
    };
    return runPlanning(inputs);
  }, [
    today,
    settings,
    holidaysQ.data,
    planLinesQ.data,
    itemsQ.data,
    levelsQ.data,
    bomQ.data,
    suppliersQ.data,
    posQ.data,
    samplesQ.data,
  ]);

  return {
    model,
    isLoading,
    error,
    settings,
    settingsSaved: settingsQ.data?.saved ?? false,
    planLines: planLinesQ.data ?? [],
    holidays: holidaysQ.data ?? [],
  };
}
